/**
 * A small, strict CSV reader (RFC 4180 style) for guest lists and other spreadsheet exports.
 *
 * Handles a UTF-8 byte-order mark, quoted cells with commas, doubled quotes and line breaks inside a cell,
 * and CR, LF or CRLF row ends. The delimiter is detected from the first line, because spreadsheets in many
 * countries export semicolons or tabs instead of commas. Pure; safe in Node and the browser.
 */

export type Delimiter = "," | ";" | "\t";

/** Picks the delimiter that splits the first line into the most cells, ignoring anything inside quotes. */
export function detectDelimiter(text: string): Delimiter {
  const first = firstLine(text);
  const counts: Record<Delimiter, number> = { ",": 0, ";": 0, "\t": 0 };
  let inQuotes = false;
  for (const ch of first) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && (ch === "," || ch === ";" || ch === "\t")) counts[ch]++;
  }
  let best: Delimiter = ",";
  for (const d of [";", "\t"] as const) if (counts[d] > counts[best]) best = d;
  return best;
}

function firstLine(text: string): string {
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && (ch === "\n" || ch === "\r")) return text.slice(0, i);
  }
  return text;
}

/** Splits CSV text into rows of cells. Fully blank lines are dropped. */
export function parseCsv(input: string, delimiter?: Delimiter): string[][] {
  const text = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;
  const d = delimiter ?? detectDelimiter(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  let touched = false; // something other than a line break was seen on this row

  const endCell = () => { row.push(cell); cell = ""; };
  const endRow = () => {
    endCell();
    if (touched || row.some((c) => c !== "")) rows.push(row);
    row = [];
    touched = false;
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++; } else inQuotes = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"' && cell === "") { inQuotes = true; touched = true; }
    else if (ch === d) { endCell(); touched = true; }
    else if (ch === "\r") { if (text[i + 1] === "\n") i++; endRow(); }
    else if (ch === "\n") endRow();
    else { cell += ch; touched = true; }
  }
  if (touched || cell !== "" || row.length) endRow();
  // A row of only empty cells (",,,") is a blank line from a spreadsheet; drop it.
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}
